// 庭ビュー: GameState.garden（純データ）を 3D 表現に反映するレイヤ。
//
// - 配置済みデコのメッシュを保持し、状態と差分同期する（毎フレーム作り直さない）
// - ホバー/選択セルのハイライト枠
// - 設置プレビュー（選択中デコの半透明ゴースト）
//
// 描画専用。状態は持たず、main から「現在の garden」と「ハイライト指示」を受けて
// シーングラフを更新するだけ。island（自転ノード）の子として動くので、デコも
// グリッドと一緒に回る。

import * as THREE from 'three';
import { PALETTE } from '../palette';
import type { GardenGrid, Orientation, IslandShape } from '../sim/garden';
import {
  GRID_SIZE,
  cellIndex,
  BLOCKED_CELLS,
  isCellInShape,
  cellLevel,
  ENTRANCE_INDEX,
  twinSurfaceY,
} from '../sim/garden';
import type { Planting, GrowthStage } from '../sim/plants';
import { isPlantDeco, stageForGrowSec } from '../sim/plants';
import type { ActiveSynergy } from '../sim/synergy';
import { CELL_SIZE, GARDEN_Y, gridToWorld } from './diorama';
import { buildDecoMesh, type PlantRender } from './deco_mesh';

interface PlacedEntry {
  decoId: string;
  orientation: Orientation;
  group: THREE.Group;
  /**
   * 植物の描画署名（段階|樹形）。これが変わったらメッシュを作り直す
   * （成長で段階が上がった・剪定で樹形が変わったときに見た目を更新するため）。
   * 非植物は undefined。
   */
  plantKey?: string;
  /** 直近に適用した活力（0..1、v0.6.0 ゆらぎ）。メッシュ再生成時の再適用に使う。 */
  vitality?: number;
  /** 直近に適用したタイルレベル（1..N、v0.7.0）。変化でレベルピップを作り直す。 */
  level?: number;
  /** レベルピップ（level≥2 で本体上に出す小さな⬆印）。 */
  levelPips?: THREE.Group | null;
}

/** ハイライト/ゴーストの表示指示。 */
export interface GardenViewHint {
  /** ハイライトするセル（ホバー/選択）。null で消す。 */
  highlight?: { gx: number; gy: number } | null;
  /** 設置プレビューを出すデコ ID（選択中デコ）。null でゴースト消去。 */
  ghostDecoId?: string | null;
  /** ゴースト/ハイライトを出すセル。 */
  ghostCell?: { gx: number; gy: number } | null;
  /** ゴーストの向き。 */
  ghostOrientation?: Orientation;
  /** ゴーストが配置可能か（不可なら赤系で示す）。 */
  ghostValid?: boolean;
}

export interface GardenView {
  /** island に add する root（呼び出し側でシーンに乗せる）。 */
  root: THREE.Group;
  /**
   * GameState.garden を反映する（差分同期）。plantings を渡すと植物デコの見た目に
   * 成長段階・樹形を反映する。tSec は成長演出（キラッ）の基準時刻（省略可）。
   */
  sync(garden: GardenGrid, plantings?: Record<string, Planting>, tSec?: number): void;
  /** ハイライト・ゴーストを更新する。 */
  setHint(hint: GardenViewHint): void;
  /**
   * 長押し撤去の対象セルと進捗（0..1）を設定する（v0.5.1）。対象のデコが震え、
   * 上に「❌」マーカーが現れる。null で解除（震えを止める）。
   */
  setRemovalCandidate(cell: { gx: number; gy: number } | null, progress: number): void;
  /**
   * セルの活力（0..1）を設定する（v0.6.0 ゆらぎ）。ゆらぐデコ（キノコ等）がしおれると
   * 1 未満になり、見た目が彩度低下＋うつむき＋縮みになる。fn 省略/常に 1 で従来の元気な姿。
   */
  setVitality(fn: ((cellIndex: number) => number) | null): void;
  /**
   * 発動中の隣接シナジー（v0.6.0）。発動ペアの間にキラキラの連結線を引く。
   * 空配列で全消去。
   */
  setSynergies(synergies: ActiveSynergy[]): void;
  /**
   * 入口→カフェの導線を「光る歩道（リボン）」として描く（v0.7.2）。
   *
   * pathCells = 入口側につながった石畳セル index の集合（path.connectedPathCells）。
   * これに入口セルとカフェ前のアンカーを足して 1 本の連続したリボンに見せ、流れる光点
   * （足あとガイド）を入口→カフェの順にアニメさせる。空配列なら歩道を消す
   *  （石畳が未連結のときは普通の石畳タイル表示に任せる）。
   */
  setPathRoute(pathCells: number[]): void;
  /**
   * 表示中の島の形状を設定する（v0.7.3）。形状が変わったらセル枠を作り直し（形なりに端を
   * 欠かす）、カフェ被りマーカーをホーム島（isHome=true）のときだけ出す。島の切替時に呼ぶ。
   */
  setShape(shape: IslandShape, isHome: boolean): void;
  /** 毎フレームのアニメーション（ゴーストの明滅など）。 */
  update(tSec: number): void;
  dispose(): void;
}

const Y = GARDEN_Y;

/** 1 セルを縁取るハイライト枠（薄板の四角リング）を作る。 */
function makeHighlightMesh(color: string): THREE.Mesh {
  const geo = new THREE.RingGeometry(CELL_SIZE * 0.34, CELL_SIZE * 0.48, 4);
  const m = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0.85, side: THREE.DoubleSide }),
  );
  m.rotation.x = -Math.PI / 2;
  m.rotation.z = Math.PI / 4; // 四角を軸に合わせる
  m.visible = false;
  return m;
}

/** セルを塗りつぶすハイライト面（選択セルの下地）。 */
function makeCellFill(color: string): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(CELL_SIZE * 0.92, CELL_SIZE * 0.92);
  const m = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0.3, side: THREE.DoubleSide }),
  );
  m.rotation.x = -Math.PI / 2;
  m.visible = false;
  return m;
}

/**
 * 配置禁止セル（カフェ被り）の地面マーカー。淡い斜線板を敷いて「ここは置けない」を
 * 静かに示す（カフェ本体が乗るので主張は控えめに）。BLOCKED_CELLS から一度だけ作る。
 */
function makeBlockedMarkers(): THREE.Group {
  const group = new THREE.Group();
  const color = new THREE.Color('#8A7AA0');
  for (const idx of BLOCKED_CELLS) {
    const gx = idx % GRID_SIZE;
    const gy = Math.floor(idx / GRID_SIZE);
    const { x, z } = gridToWorld(gx, gy);
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(CELL_SIZE * 0.9, CELL_SIZE * 0.9),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.22, side: THREE.DoubleSide }),
    );
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, GARDEN_Y + 0.005, z);
    group.add(m);
  }
  return group;
}

/**
 * セル枠（グリッド線）を「空き＆有効セルだけ」描くための層を作る。
 *
 * v0.5.1 ユーザーレビュー反映: グリッドを 1 枚の GridHelper でなくセル単位の枠
 * （正方リング）で描く。これにより (a) デコが置いてあるセルは枠を隠せる、
 * (b) 島の円形上面に乗らない無効セルは枠自体を作らない（円なりに端が欠ける）。
 *
 * 各セルの枠は薄い四角の輪郭線（LineSegments）。占有状態は sync が visible で切り替える。
 * 戻り値は「セル index → 枠オブジェクト」のマップ（有効・非カフェ被りセルのみ）。
 *
 * v0.7.3: 表示中の島の形状（shape）の有効セルだけ枠を描く（形なりに端が欠ける）。
 * カフェ被りセルを除外するのはホーム島（applyBlocked=true）だけ（拡張島はカフェ無し）。
 */
function makeGridFrames(
  shape: IslandShape,
  applyBlocked: boolean,
): { group: THREE.Group; byCell: Map<number, THREE.LineSegments> } {
  const group = new THREE.Group();
  const byCell = new Map<number, THREE.LineSegments>();
  const color = new THREE.Color(PALETTE.gridLine);
  // 1 セルの枠（中心原点の正方リング）の頂点。CELL の少し内側に描いて隙間で格子に見せる。
  const h = CELL_SIZE * 0.46;
  const ringPts = [
    [-h, 0, -h], [h, 0, -h],
    [h, 0, -h], [h, 0, h],
    [h, 0, h], [-h, 0, h],
    [-h, 0, h], [-h, 0, -h],
  ];
  const baseGeo = new THREE.BufferGeometry();
  baseGeo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(ringPts.flat(), 3),
  );
  for (let gy = 0; gy < GRID_SIZE; gy++) {
    for (let gx = 0; gx < GRID_SIZE; gx++) {
      // 島の形状外（無効セル）とカフェ被りセル（ホーム島のみ）には枠を描かない。
      if (!isCellInShape(gx, gy, shape)) continue;
      const idx = cellIndex(gx, gy);
      if (applyBlocked && BLOCKED_CELLS.has(idx)) continue;
      const { x, z } = gridToWorld(gx, gy);
      const seg = new THREE.LineSegments(
        baseGeo, // ジオメトリは共有（位置はオブジェクトの transform で動かす）
        // v0.7.2: 空きセル枠は控えめに（0.5→0.28）。光る歩道（導線）と差をつけ、
        // 「どこにでも置けるグリッド」と「客が歩く道」をはっきり別物に見せる。
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.28 }),
      );
      // v0.7.6: twin 島は左右ディスクの高低差ぶん枠 Y をオフセット（デコと同じ高さに乗せる）。
      const dy = shape === 'twin' ? twinSurfaceY(x) : 0;
      seg.position.set(x, GARDEN_Y + 0.004 + dy, z);
      group.add(seg);
      byCell.set(idx, seg);
    }
  }
  return { group, byCell };
}

/**
 * タイルレベル（2..N）を示す小さな「⬆」ピップ群（v0.7.0）。デコ本体の上に浮かべて
 * level-1 個の小さな金色の三角を横一列に並べる（level 1 は何も出さない）。
 * 返る Group はセル原点・本体の上（Y+高さ）に置く前提で position を呼び出し側が設定。
 */
function makeLevelPips(level: number): THREE.Group | null {
  if (level <= 1) return null;
  const g = new THREE.Group();
  const n = level - 1; // level 2 → 1 個, level 5 → 4 個
  const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color('#FFD79A') });
  const w = 0.06;
  for (let i = 0; i < n; i++) {
    // 上向き三角（ConeGeometry 3 角）。
    const pip = new THREE.Mesh(new THREE.ConeGeometry(w * 0.6, w, 3), mat);
    pip.position.set((i - (n - 1) / 2) * (w * 1.4), 0, 0);
    g.add(pip);
  }
  return g;
}

/** Group とその全子マテリアル/ジオメトリを破棄。 */
function disposeGroup(g: THREE.Group): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat?.dispose();
  });
}

/** ゴースト用に Group を半透明化する。 */
function makeGhostTranslucent(g: THREE.Group, valid: boolean): void {
  const tint = valid ? null : new THREE.Color('#E89BA8');
  g.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const mat = mesh.material as THREE.Material | undefined;
    if (!mat) return;
    const sm = mat as THREE.MeshStandardMaterial & THREE.MeshBasicMaterial;
    sm.transparent = true;
    sm.opacity = 0.5;
    sm.depthWrite = false;
    if (tint && sm.color) sm.color = tint.clone();
  });
}

export function createGardenView(): GardenView {
  const root = new THREE.Group();

  // 配置済みデコの層。グリッド index → エントリ。
  const placed = new Map<number, PlacedEntry>();
  const decoLayer = new THREE.Group();
  root.add(decoLayer);

  // 配置禁止セル（カフェ被り）の静的マーカー。ホーム島でだけ表示する（拡張島はカフェ無し）。
  const blockedMarkers = makeBlockedMarkers();
  root.add(blockedMarkers);

  // セル枠（空き＆有効セルだけ描く。占有セルは sync で枠を隠す）。
  // 表示中の島の形状で作り直すので let。初期はホーム島（round）。
  let currentShape: IslandShape = 'round';
  let currentApplyBlocked = true;
  let grid = makeGridFrames(currentShape, currentApplyBlocked);
  root.add(grid.group);

  /**
   * セル (gx,gy) の **表示** Y（v0.7.6）。twin 島だけ左右ディスクの高低差ぶんの視覚オフセットを
   * 足す（左 +delta / 右 -delta、橋帯は線形補間）。非 twin は GARDEN_Y 平面のまま（オフセット 0）。
   * デコ・グリッド枠・レベルピップ・シナジー端点・撤去マーク・ゴースト等、セル Y を使う全箇所が
   * これを通すことでディスクの高さに正しく乗る（浮き/沈みを防ぐ）。純粋に見た目だけ（ピッキングは
   * 平らな pickPlane のまま）。
   */
  function cellSurfaceY(gx: number, gy: number): number {
    if (currentShape !== 'twin') return Y;
    const { x } = gridToWorld(gx, gy);
    return Y + twinSurfaceY(x);
  }

  /** 形状（と home/拡張）を切り替える: セル枠を作り直し、カフェ被りマーカーを home だけ出す。 */
  function setShape(shape: IslandShape, isHome: boolean): void {
    blockedMarkers.visible = isHome; // カフェ被りマーカーはホーム島のみ
    if (shape === currentShape && isHome === currentApplyBlocked) return; // 不変なら据え置き
    currentShape = shape;
    currentApplyBlocked = isHome;
    // 旧枠を破棄して作り直す（ジオメトリ・マテリアルを dispose）。
    root.remove(grid.group);
    disposeGroup(grid.group);
    grid = makeGridFrames(shape, isHome);
    root.add(grid.group);
  }

  // ハイライト（ホバー枠 + 選択下地）
  const hoverRing = makeHighlightMesh(PALETTE.warmOrange);
  const cellFill = makeCellFill(PALETTE.crystalBlue);
  root.add(hoverRing, cellFill);

  // ゴースト層
  const ghostLayer = new THREE.Group();
  root.add(ghostLayer);
  let ghostKey = ''; // 現在のゴーストの (decoId|orientation|valid) 署名
  let ghostBaseY = Y; // ゴーストセルの表面 Y（twin の高低差を反映。update の浮遊基準に使う）

  // --- 長押し撤去マーカー（❌、v0.5.1）---
  // 赤い × を 2 本のバツ棒で作る。対象セルの上に出して震わせ、進捗で大きくする。
  const removalMark = new THREE.Group();
  {
    const barGeo = new THREE.BoxGeometry(0.26, 0.06, 0.06);
    const barMat = new THREE.MeshBasicMaterial({ color: new THREE.Color('#E85A6A') });
    const b1 = new THREE.Mesh(barGeo, barMat);
    b1.rotation.z = Math.PI / 4;
    const b2 = new THREE.Mesh(barGeo, barMat);
    b2.rotation.z = -Math.PI / 4;
    removalMark.add(b1, b2);
  }
  removalMark.visible = false;
  root.add(removalMark);
  // 撤去候補の状態（セル・進捗）。update が震え・拡大に反映する。
  let removalCell: { gx: number; gy: number } | null = null;
  let removalProgress = 0;

  /** 直前に震わせていたセルのデコを基準位置へ戻す（候補解除/切替時の後始末）。 */
  function restoreShaken(): void {
    if (!removalCell) return;
    const idx = cellIndex(removalCell.gx, removalCell.gy);
    const entry = placed.get(idx);
    if (entry) {
      const { x, z } = gridToWorld(removalCell.gx, removalCell.gy);
      entry.group.position.set(x, cellSurfaceY(removalCell.gx, removalCell.gy), z);
    }
  }

  function setRemovalCandidate(cell: { gx: number; gy: number } | null, progress: number): void {
    // 対象が変わる/解除されるとき、前の対象の震えを戻す。
    if (!cell || !removalCell || cell.gx !== removalCell.gx || cell.gy !== removalCell.gy) {
      restoreShaken();
    }
    removalCell = cell;
    removalProgress = progress;
    removalMark.visible = cell != null;
    if (cell) {
      const { x, z } = gridToWorld(cell.gx, cell.gy);
      removalMark.position.set(x, cellSurfaceY(cell.gx, cell.gy) + 0.7, z);
    }
  }

  // デコは俯瞰カメラだと小さく見えるので少し持ち上げて存在感を出す。
  const DECO_SCALE = 1.35;

  // 成長演出（キラッ）の小さなパーティクル群。プールせず短命に作って消す。
  interface Sparkle {
    group: THREE.Group;
    bornT: number;
  }
  const sparkles: Sparkle[] = [];
  const SPARKLE_SEC = 1.1;

  // --- ゆらぎ（しおれ）---
  // セル index → 活力(0..1)。1 未満のセルは彩度低下＋うつむき＋縮みで「しおれ」を表現。
  let vitalityFn: ((cellIndex: number) => number) | null = null;

  /**
   * セルのデコ Group にしおれ表現を適用する。活力 1.0 で元気（無変形）、低いほど
   * しぼんで（縮小）うつむき（前傾）、色の彩度を落とす。元の色を覚えておき毎回そこから作る。
   */
  function applyVitality(entry: PlacedEntry, gx: number, gy: number): void {
    const idx = cellIndex(gx, gy);
    const vit = vitalityFn ? vitalityFn(idx) : 1.0;
    entry.vitality = vit;
    // タイルレベル（v0.7.0）でわずかに大きく＝「育った」感（level 1→1.0, 5→~1.24）。
    const lvlScale = 1 + 0.06 * ((entry.level ?? 1) - 1);
    const base = DECO_SCALE * lvlScale;
    // スケール: 元気 1.0 → しおれ 0.78。うつむき: 最大 ~22°前傾。
    entry.group.scale.set(base, base * (0.7 + 0.3 * vit), base);
    entry.group.rotation.x = (1 - vit) * 0.38; // 前傾
    // 色の彩度を落とす（HSL の S を vit に応じて下げる）。元色をキャッシュ。
    entry.group.traverse((o) => {
      const m = o as THREE.Mesh;
      const mat = m.material as (THREE.MeshStandardMaterial | THREE.MeshBasicMaterial) | undefined;
      if (!mat || !('color' in mat)) return;
      const anyMat = mat as THREE.MeshStandardMaterial & { __baseColor?: THREE.Color };
      if (!anyMat.__baseColor) anyMat.__baseColor = anyMat.color.clone();
      const base = anyMat.__baseColor;
      if (vit >= 0.999) {
        anyMat.color.copy(base);
      } else {
        const hsl = { h: 0, s: 0, l: 0 };
        base.getHSL(hsl);
        // 彩度を活力ぶんに、明度も少し落として「しなびた」見た目に。
        anyMat.color.setHSL(hsl.h, hsl.s * (0.35 + 0.65 * vit), hsl.l * (0.8 + 0.2 * vit));
      }
    });
  }

  function placeAt(entry: PlacedEntry, gx: number, gy: number): void {
    const { x, z } = gridToWorld(gx, gy);
    entry.group.position.set(x, cellSurfaceY(gx, gy), z);
    entry.group.rotation.y = (entry.orientation * Math.PI) / 2;
    entry.group.scale.setScalar(DECO_SCALE);
    // 配置直後にしおれ表現を反映（rotation.x / scale を上書きする）。
    applyVitality(entry, gx, gy);
    applyLevelPips(entry, gx, gy);
  }

  /** エントリのレベルに応じてレベルピップを作り直し、本体の上に浮かべる。 */
  function applyLevelPips(entry: PlacedEntry, gx: number, gy: number): void {
    const level = entry.level ?? 1;
    // 既存ピップを破棄。
    if (entry.levelPips) {
      decoLayer.remove(entry.levelPips);
      disposeGroup(entry.levelPips);
      entry.levelPips = null;
    }
    const pips = makeLevelPips(level);
    if (!pips) return;
    const { x, z } = gridToWorld(gx, gy);
    pips.position.set(x, cellSurfaceY(gx, gy) + 0.72, z);
    pips.rotation.x = -Math.PI / 2.4; // 俯瞰カメラ向きに少し寝かせる
    decoLayer.add(pips);
    entry.levelPips = pips;
  }

  // --- 隣接シナジーの連結線（v0.6.0）---
  // 発動中ペアの間にキラキラの細い線を引く。点滅で「効いている」を静かに示す。
  const synergyLayer = new THREE.Group();
  root.add(synergyLayer);
  let synergyLines: THREE.Line[] = [];

  function setSynergies(synergies: ActiveSynergy[]): void {
    // 既存を破棄して作り直す（発動ペアは頻繁には変わらないので毎回作っても安い）。
    for (const l of synergyLines) {
      synergyLayer.remove(l);
      l.geometry.dispose();
      (l.material as THREE.Material).dispose();
    }
    synergyLines = [];
    for (const s of synergies) {
      const ag = { gx: s.a % GRID_SIZE, gy: Math.floor(s.a / GRID_SIZE) };
      const bg = { gx: s.b % GRID_SIZE, gy: Math.floor(s.b / GRID_SIZE) };
      const aw = gridToWorld(ag.gx, ag.gy);
      const bw = gridToWorld(bg.gx, bg.gy);
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(aw.x, cellSurfaceY(ag.gx, ag.gy) + 0.34, aw.z),
        new THREE.Vector3(bw.x, cellSurfaceY(bg.gx, bg.gy) + 0.34, bw.z),
      ]);
      const line = new THREE.Line(
        geo,
        new THREE.LineBasicMaterial({
          color: new THREE.Color(PALETTE.cafeWindowGlowCore),
          transparent: true,
          opacity: 0.7,
        }),
      );
      synergyLayer.add(line);
      synergyLines.push(line);
    }
  }

  // --- 光る歩道（導線リボン）と足あとガイド（v0.7.2）---
  // 連結した石畳の上に、空きセル枠とははっきり違う「リボン状の歩道」を重ねて描く。
  //   - 各歩道セルに明るいクリーム色の板（縁＝発光リング）を敷く＝夜でも沈まない。
  //   - 入口 → カフェの順に並べたアンカー列に沿って、流れる光点（足あと）を走らせる。
  // pathOrder は入口に近い順（BFS 距離順）に並べたワールド XZ アンカー列。
  const pathLayer = new THREE.Group();
  pathLayer.position.y = Y + 0.012; // セル枠（+0.004）より上に重ねて埋もれさせない
  root.add(pathLayer);
  let pathDots: THREE.Mesh[] = []; // 流れる光点（足あとガイド）
  let pathOrder: THREE.Vector3[] = []; // 入口→カフェのアンカー列（光点が辿る）
  let ribbonCells = new Set<number>(); // リボン化した石畳セル（素の石畳メッシュを隠す）

  /** 歩道セル 1 枚の発光板（明るい床＋縁リング）を作って pathLayer に足す。 */
  function addPathTile(x: number, z: number): void {
    // 明るい床（夜でも沈まないベタ塗り）。
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(CELL_SIZE * 0.82, CELL_SIZE * 0.82),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color('#F3E8CF'),
        transparent: true,
        opacity: 0.92,
        side: THREE.DoubleSide,
      }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(x, 0, z);
    pathLayer.add(floor);
    // 淡く発光する縁（リボンのシーム）。
    const rim = new THREE.Mesh(
      new THREE.RingGeometry(CELL_SIZE * 0.38, CELL_SIZE * 0.48, 4),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(PALETTE.cafeWindowGlowCore),
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
      }),
    );
    rim.rotation.x = -Math.PI / 2;
    rim.rotation.z = Math.PI / 4;
    rim.position.set(x, 0.003, z);
    pathLayer.add(rim);
  }

  /** 既存の歩道リボン・光点を破棄して空にする。 */
  function clearPathLayer(): void {
    while (pathLayer.children.length) {
      const c = pathLayer.children[0];
      pathLayer.remove(c);
      const m = c as THREE.Mesh;
      m.geometry?.dispose();
      (m.material as THREE.Material | undefined)?.dispose();
    }
    pathDots = [];
    pathOrder = [];
  }

  /** リボン化セルの素の石畳メッシュ表示を更新する（リボン上の灰色タイルを隠す/戻す）。 */
  function applyRibbonHiding(): void {
    for (const [idx, entry] of placed) {
      if (entry.decoId === 'stone_tile') {
        entry.group.visible = !ribbonCells.has(idx);
      }
    }
  }

  function setPathRoute(pathCells: number[]): void {
    clearPathLayer();
    // 旧リボンの隠しを解除してから作り直す。
    ribbonCells = new Set(pathCells);
    applyRibbonHiding();
    if (pathCells.length === 0) return;

    // 入口に近い順（BFS 距離順）に石畳を並べ替えて、連続したリボン＋光点の進行方向を作る。
    const set = new Set(pathCells);
    const egx = ENTRANCE_INDEX % GRID_SIZE;
    const egy = Math.floor(ENTRANCE_INDEX / GRID_SIZE);
    const ordered: number[] = [];
    const visited = new Set<number>();
    // 入口に隣接する石畳から BFS（path.ts と同じ連結成分）。
    const queue: number[] = [];
    const nbr = (idx: number): number[] => {
      const gx = idx % GRID_SIZE;
      const gy = Math.floor(idx / GRID_SIZE);
      const out: number[] = [];
      if (gx > 0) out.push(idx - 1);
      if (gx + 1 < GRID_SIZE) out.push(idx + 1);
      if (gy > 0) out.push(idx - GRID_SIZE);
      if (gy + 1 < GRID_SIZE) out.push(idx + GRID_SIZE);
      return out;
    };
    for (const n of nbr(cellIndex(egx, egy))) {
      if (set.has(n) && !visited.has(n)) { visited.add(n); queue.push(n); }
    }
    while (queue.length) {
      const idx = queue.shift()!;
      ordered.push(idx);
      for (const n of nbr(idx)) {
        if (set.has(n) && !visited.has(n)) { visited.add(n); queue.push(n); }
      }
    }
    // BFS で漏れたセル（孤立した連結端）も末尾に足しておく。
    for (const idx of pathCells) if (!visited.has(idx)) ordered.push(idx);

    // リボン本体（各石畳セルに発光板）。入口・カフェ前のアンカーも光点の通り道に足す。
    for (const idx of ordered) {
      const w = gridToWorld(idx % GRID_SIZE, Math.floor(idx / GRID_SIZE));
      addPathTile(w.x, w.z);
    }

    // 光点アンカー列: 入口 → 石畳（距離順）→ カフェ前。
    const ew = gridToWorld(egx, egy);
    pathOrder.push(new THREE.Vector3(ew.x, 0, ew.z));
    for (const idx of ordered) {
      const w = gridToWorld(idx % GRID_SIZE, Math.floor(idx / GRID_SIZE));
      pathOrder.push(new THREE.Vector3(w.x, 0, w.z));
    }
    // カフェ前のアンカー（占有ブロックの手前側 (5,6) 付近）。
    const cw = gridToWorld(5, 6);
    pathOrder.push(new THREE.Vector3(cw.x, 0, cw.z));

    // 流れる光点（足あとガイド）。小さな明るい玉を 4 つ、列に沿って循環させる。
    const DOTS = 4;
    for (let i = 0; i < DOTS; i++) {
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.07, 8, 6),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(PALETTE.warmOrange),
          transparent: true,
          opacity: 0.9,
        }),
      );
      pathLayer.add(dot);
      pathDots.push(dot);
    }
  }

  /** 光点（足あとガイド）を入口→カフェの順に流す。pathOrder の弧長パラメータで補間。 */
  function updatePathDots(tSec: number): void {
    if (pathOrder.length < 2 || pathDots.length === 0) return;
    const segs = pathOrder.length - 1;
    const cycle = 3.0; // 入口→カフェを 3 秒で 1 周
    for (let i = 0; i < pathDots.length; i++) {
      // 等間隔の位相で循環。t は 0..1（入口=0, カフェ=1）。
      const t = ((tSec / cycle + i / pathDots.length) % 1 + 1) % 1;
      const f = t * segs;
      const si = Math.min(segs - 1, Math.floor(f));
      const local = f - si;
      const a = pathOrder[si];
      const b = pathOrder[si + 1];
      const dot = pathDots[i];
      dot.position.set(
        a.x + (b.x - a.x) * local,
        0.05 + Math.sin((tSec + i) * 3) * 0.02,
        a.z + (b.z - a.z) * local,
      );
      // 入口側で明るく現れ、カフェ手前で消える（流れる感）。
      const fade = Math.sin(t * Math.PI);
      (dot.material as THREE.MeshBasicMaterial).opacity = 0.35 + fade * 0.6;
    }
  }

  function setVitality(fn: ((cellIndex: number) => number) | null): void {
    vitalityFn = fn;
    // 既存の配置に即時反映（しおれ⇔復活の変化を毎回の評価で反映する）。
    for (const [idx, entry] of placed) {
      const gx = idx % GRID_SIZE;
      const gy = Math.floor(idx / GRID_SIZE);
      applyVitality(entry, gx, gy);
    }
  }

  /** 成長段階の序列（演出で「上がった」判定に使う）。 */
  function stageRank(stage: GrowthStage): number {
    return { sprout: 0, sapling: 1, mature: 2, ancient: 3 }[stage];
  }

  /** 植物セルの描画コンテキストと署名を作る（plantings から）。非植物は null。 */
  function plantContext(
    decoId: string,
    idx: number,
    plantings: Record<string, Planting> | undefined,
  ): { plant: PlantRender; key: string; stage: GrowthStage } | null {
    if (!isPlantDeco(decoId)) return null;
    const p = plantings?.[String(idx)];
    const growSec = p ? p.growSec : 0;
    const form = p ? p.form : { branches: 0.5, bend: 0.5, lush: 0.5, trained: 0 };
    const stage = stageForGrowSec(growSec);
    // 樹形は剪定で変わるので署名に含める（変化でメッシュを作り直す）。
    const fk = `${form.branches.toFixed(2)},${form.bend.toFixed(2)},${form.lush.toFixed(2)},${form.trained.toFixed(2)}`;
    return { plant: { stage, form }, key: `${stage}|${fk}`, stage };
  }

  /** セル中心に成長キラッのスパークルを撒く。 */
  function spawnGrowSparkle(gx: number, gy: number, tSec: number): void {
    const { x, z } = gridToWorld(gx, gy);
    const group = new THREE.Group();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const s = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.04),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(PALETTE.warmOrange), transparent: true, opacity: 1 }),
      );
      s.position.set(Math.cos(a) * 0.12, 0.5, Math.sin(a) * 0.12);
      group.add(s);
    }
    group.position.set(x, Y, z);
    root.add(group);
    sparkles.push({ group, bornT: tSec });
  }

  function sync(garden: GardenGrid, plantings?: Record<string, Planting>, tSec = 0): void {
    // 1) 既存エントリのうち、状態と食い違うものを撤去
    for (const [idx, entry] of placed) {
      const cell = garden[idx];
      if (!cell || cell.decoId !== entry.decoId) {
        decoLayer.remove(entry.group);
        disposeGroup(entry.group);
        if (entry.levelPips) {
          decoLayer.remove(entry.levelPips);
          disposeGroup(entry.levelPips);
        }
        placed.delete(idx);
      }
    }
    // 2) 状態にあって未生成のセルを作る / 向き・成長・樹形が変わったものを更新
    for (let gy = 0; gy < GRID_SIZE; gy++) {
      for (let gx = 0; gx < GRID_SIZE; gx++) {
        const idx = cellIndex(gx, gy);
        const cell = garden[idx];
        if (!cell) continue;
        const ctx = plantContext(cell.decoId, idx, plantings);
        const existing = placed.get(idx);

        const level = cellLevel(cell);

        if (existing && existing.decoId === cell.decoId) {
          // 植物: 段階 or 樹形が変わったらメッシュを作り直す
          const plantChanged = ctx != null && existing.plantKey !== ctx.key;
          const levelChanged = (existing.level ?? 1) !== level;
          if (plantChanged) {
            // 段階が「上がった」ときだけキラッ演出（剪定だけの変化では出さない）
            const prevStage = existing.plantKey?.split('|')[0] as GrowthStage | undefined;
            const grew =
              prevStage != null && prevStage !== ctx!.stage && stageRank(ctx!.stage) > stageRank(prevStage);
            decoLayer.remove(existing.group);
            disposeGroup(existing.group);
            const group = buildDecoMesh(cell.decoId, ctx!.plant);
            existing.group = group;
            existing.plantKey = ctx!.key;
            existing.level = level;
            placeAt(existing, gx, gy);
            decoLayer.add(group);
            if (grew) spawnGrowSparkle(gx, gy, tSec);
          } else if (existing.orientation !== cell.orientation || levelChanged) {
            existing.orientation = cell.orientation;
            existing.level = level;
            placeAt(existing, gx, gy);
            // アップグレード時は「育った」キラッ演出を出す（段階上昇と同じ達成感）。
            if (levelChanged) spawnGrowSparkle(gx, gy, tSec);
          }
          continue;
        }

        const group = buildDecoMesh(cell.decoId, ctx?.plant);
        const entry: PlacedEntry = {
          decoId: cell.decoId,
          orientation: cell.orientation,
          group,
          level,
          plantKey: ctx?.key,
        };
        placeAt(entry, gx, gy);
        decoLayer.add(group);
        placed.set(idx, entry);
      }
    }

    // 3) セル枠の表示更新: デコが置いてあるセルの枠は隠し、空きセルだけ枠を出す。
    for (const [idx, seg] of grid.byCell) {
      seg.visible = garden[idx] == null;
    }

    // 4) リボン化した石畳セルの素の灰色タイルを隠す（光る歩道で置き換えるため）。
    applyRibbonHiding();
  }

  function setHint(hint: GardenViewHint): void {
    // --- ハイライト ---
    const hl = hint.highlight ?? hint.ghostCell ?? null;
    if (hl) {
      const { x, z } = gridToWorld(hl.gx, hl.gy);
      hoverRing.position.set(x, Y + 0.02, z);
      cellFill.position.set(x, Y + 0.01, z);
      hoverRing.visible = true;
      cellFill.visible = true;
    } else {
      hoverRing.visible = false;
      cellFill.visible = false;
    }

    // --- ゴースト ---
    const decoId = hint.ghostDecoId ?? null;
    const cell = hint.ghostCell ?? null;
    if (decoId && cell) {
      const valid = hint.ghostValid !== false;
      const orientation = hint.ghostOrientation ?? 0;
      const key = `${decoId}|${orientation}|${valid}`;
      if (key !== ghostKey) {
        // ゴーストを作り直す（デコ or 向き or 妥当性が変わったとき）
        while (ghostLayer.children.length) {
          const c = ghostLayer.children[0] as THREE.Group;
          ghostLayer.remove(c);
          disposeGroup(c);
        }
        const g = buildDecoMesh(decoId);
        g.scale.setScalar(DECO_SCALE);
        makeGhostTranslucent(g, valid);
        ghostLayer.add(g);
        ghostKey = key;
      }
      const { x, z } = gridToWorld(cell.gx, cell.gy);
      ghostBaseY = cellSurfaceY(cell.gx, cell.gy);
      ghostLayer.position.set(x, ghostBaseY, z);
      ghostLayer.rotation.y = (orientation * Math.PI) / 2;
      ghostLayer.visible = true;
    } else {
      ghostLayer.visible = false;
      ghostKey = '';
      while (ghostLayer.children.length) {
        const c = ghostLayer.children[0] as THREE.Group;
        ghostLayer.remove(c);
        disposeGroup(c);
      }
    }
  }

  function update(tSec: number): void {
    // ハイライトとゴーストをゆっくり明滅させて「今ここ」を示す
    const pulse = 0.6 + Math.sin(tSec * 4) * 0.25;
    (hoverRing.material as THREE.MeshBasicMaterial).opacity = pulse;
    if (ghostLayer.visible) {
      // ゴーストの浮遊明滅。twin ではゴーストセルの表面 Y を基準にする（setHint で設定した x/z は保持）。
      ghostLayer.position.y = ghostBaseY + Math.sin(tSec * 3) * 0.03;
    }

    // --- 長押し撤去: 対象デコを震わせ、❌ マーカーをぷるぷるさせる ---
    // 対象セルのデコ Group を毎フレーム基準位置へ戻してから震えを足す
    // （別の sync で placeAt が呼ばれない限り基準位置は不変だが、安全に再計算する）。
    if (removalCell) {
      const idx = cellIndex(removalCell.gx, removalCell.gy);
      const entry = placed.get(idx);
      if (entry) {
        const { x, z } = gridToWorld(removalCell.gx, removalCell.gy);
        const baseY = cellSurfaceY(removalCell.gx, removalCell.gy);
        // 震えは進捗が進むほど大きく速く（ﾆｭﾆｭﾝと崩れる予感）。
        const amp = 0.015 + removalProgress * 0.05;
        entry.group.position.x = x + Math.sin(tSec * 38) * amp;
        entry.group.position.z = z + Math.cos(tSec * 31) * amp;
        entry.group.position.y = baseY + Math.abs(Math.sin(tSec * 22)) * 0.02 * removalProgress;
      }
      // ❌ マーカー: 進捗で少し拡大＋小刻みに回って「迫ってる」感を出す。
      const s = 0.8 + removalProgress * 0.7 + Math.sin(tSec * 18) * 0.06;
      removalMark.scale.setScalar(s);
      removalMark.rotation.z = Math.sin(tSec * 16) * 0.12;
      // 俯瞰カメラへ向くよう Y はゆるく回さず固定。
    }

    // 光る歩道の足あとガイド（入口→カフェへ流れる光点）。
    updatePathDots(tSec);

    // シナジー連結線: ゆっくり明滅させて「効いている」をさりげなく示す。
    if (synergyLines.length) {
      const o = 0.45 + Math.sin(tSec * 3.2) * 0.3;
      for (const l of synergyLines) (l.material as THREE.LineBasicMaterial).opacity = o;
    }

    // 成長キラッ: 撒いた粒を外へ広げながらフェードアウトして消す。
    for (let i = sparkles.length - 1; i >= 0; i--) {
      const sp = sparkles[i];
      const age = tSec - sp.bornT;
      if (age >= SPARKLE_SEC || age < 0) {
        root.remove(sp.group);
        disposeGroup(sp.group);
        sparkles.splice(i, 1);
        continue;
      }
      const t = age / SPARKLE_SEC;
      const spread = 1 + t * 1.6;
      sp.group.scale.setScalar(spread);
      sp.group.position.y = Y + t * 0.5;
      for (const child of sp.group.children) {
        const m = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
        m.opacity = 1 - t;
        child.rotation.y = tSec * 6;
      }
    }
  }

  function dispose(): void {
    for (const entry of placed.values()) {
      disposeGroup(entry.group);
      if (entry.levelPips) disposeGroup(entry.levelPips);
    }
    placed.clear();
    for (const sp of sparkles) disposeGroup(sp.group);
    sparkles.length = 0;
    clearPathLayer();
    disposeGroup(decoLayer);
    disposeGroup(blockedMarkers);
    disposeGroup(grid.group);
    disposeGroup(removalMark);
    disposeGroup(ghostLayer);
    for (const l of synergyLines) {
      l.geometry.dispose();
      (l.material as THREE.Material).dispose();
    }
    synergyLines = [];
    hoverRing.geometry.dispose();
    (hoverRing.material as THREE.Material).dispose();
    cellFill.geometry.dispose();
    (cellFill.material as THREE.Material).dispose();
  }

  return {
    root,
    sync,
    setHint,
    setRemovalCandidate,
    setVitality,
    setSynergies,
    setPathRoute,
    setShape,
    update,
    dispose,
  };
}
